import Cookies from 'js-cookie';

// Cookie auth is now the default (and only) storage mechanism. Tokens are
// still mirrored into localStorage so any remaining legacy code that reads
// `apollo-token` continues to work until it is fully migrated.

// NOTE: If you are looking for the old `USE_COOKIE_AUTH` flag – it has been
// removed as of July 2025. All reads now prefer cookies and transparently
// fall back to localStorage; all writes update BOTH locations.

// Cookie configuration for secure storage
const COOKIE_OPTIONS = {
  secure: process.env.NODE_ENV === 'production', // Only secure in production
  sameSite: 'strict' as const,
  path: '/',
  expires: 7 // 7 days for access token cookies
};

const REFRESH_COOKIE_OPTIONS = {
  ...COOKIE_OPTIONS,
  expires: 30 // 30 days for refresh token cookies
};

// Storage keys
const ACCESS_TOKEN_COOKIE_KEY = 'access_token';
const REFRESH_TOKEN_COOKIE_KEY = 'refresh_token';

// For legacy localStorage mode we keep the original keys so existing code continues to work
const ACCESS_TOKEN_LS_KEY = 'apollo-token';
const REFRESH_TOKEN_LS_KEY = 'apollo-refresh-token';

/**
 * Get access token from secure storage
 */
export const getAccessToken = (): string | null => {
  // Prefer cookie; fall back to legacy localStorage key
  return Cookies.get(ACCESS_TOKEN_COOKIE_KEY) || localStorage.getItem(ACCESS_TOKEN_LS_KEY) || null;
};

/**
 * Set access token in secure storage
 */
export const setAccessToken = (token: string): void => {
  // Always set cookie **and** legacy LS key for backward compatibility
  Cookies.set(ACCESS_TOKEN_COOKIE_KEY, token, COOKIE_OPTIONS);
  localStorage.setItem(ACCESS_TOKEN_LS_KEY, token);
};

/**
 * Get refresh token from secure storage
 */
export const getRefreshToken = (): string | null => {
  return (
    Cookies.get(REFRESH_TOKEN_COOKIE_KEY) || localStorage.getItem(REFRESH_TOKEN_LS_KEY) || null
  );
};

/**
 * Set refresh token in secure storage
 */
export const setRefreshToken = (token: string): void => {
  Cookies.set(REFRESH_TOKEN_COOKIE_KEY, token, REFRESH_COOKIE_OPTIONS);
  localStorage.setItem(REFRESH_TOKEN_LS_KEY, token);
};

/**
 * Clear all authentication data
 */
export const clearAuth = (): void => {
  // Include both frontend (apollo-*) and backend (access_token, refresh_token)
  const keysToRemove = [
    ACCESS_TOKEN_COOKIE_KEY,
    REFRESH_TOKEN_COOKIE_KEY,
    ACCESS_TOKEN_LS_KEY,
    REFRESH_TOKEN_LS_KEY,
    'access_token',
    'refresh_token',
    'permissions',
    'OrganizationPermissions',
    'statistics',
    'user'
  ];

  keysToRemove.forEach(key => {
    Cookies.remove(key, { path: '/' });
    localStorage.removeItem(key);
  });
};

// Generic helpers continue to use **localStorage only**.  Non-sensitive UI
// state and feature flags remain there until a later cleanup phase.
export const getItem = (key: string): string | null => {
  return localStorage.getItem(key);
};

export const setItem = (key: string, value: string): void => {
  localStorage.setItem(key, value);
};

export const removeItem = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Check if user is authenticated (has access token)
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Get current storage mode for debugging
 */
export const getStorageMode = (): 'cookies' | 'cookies+localStorage' => {
  return 'cookies';
};

// Default export object containing all methods
const secureStorage = {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearAuth,
  getItem,
  setItem,
  removeItem,
  isAuthenticated,
  getStorageMode
};

export default secureStorage;
